import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import LandingSection from '../../../MyComponents/LandingSections/LandingSection';
import SectionEyebrow from '../../../MyComponents/LandingSections/SectionEyebrow';
import SectionHeading from '../../../MyComponents/LandingSections/SectionHeading';
import { FOR_SCHOOLS_LINK } from '../../../common/urlLinks';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../../constants';

// The ids match the design's anchor targets so in-page links keep working if the
// nav ever points at them.
const CARDS = [
    { key: 'classroom', id: 'la-clasa', theme: 'light', bullets: 4, blob: 'disc' },
    { key: 'online', id: 'online', theme: 'dark', bullets: 5, blob: 'ringStrong' },
    { key: 'school', id: 'in-scoala', theme: 'amber', bullets: 5, blob: 'ringSoft', cta: FOR_SCHOOLS_LINK },
];

const useStyles = makeStyles(() => ({
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
        gap: '22px',
        alignItems: 'stretch',
    },
    card: {
        boxSizing: 'border-box',
        position: 'relative',
        // Clips the decorative blob that bleeds off the bottom-right corner.
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        padding: '42px',
        borderRadius: '28px',
    },
    light: {
        border: `1px solid ${REDESIGN_COLORS.sand}`,
        background: REDESIGN_COLORS.white,
    },
    dark: {
        color: REDESIGN_COLORS.white,
        background: REDESIGN_COLORS.dark,
    },
    amber: {
        border: `1px solid ${REDESIGN_COLORS.amberBorder}`,
        background: REDESIGN_COLORS.amberSurface,
    },
    eyebrow: {
        alignSelf: 'flex-start',
        marginBottom: '15px',
    },
    eyebrowOnDark: {
        color: REDESIGN_COLORS.mist,
        background: 'rgba(255, 255, 255, 0.1)',
    },
    title: {
        margin: '0 0 14px',
        maxWidth: '520px',
        fontSize: 'clamp(1.55rem, 2.6vw, 2.15rem)',
        lineHeight: 1.1,
        letterSpacing: '-0.03em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    titleOnDark: {
        color: REDESIGN_COLORS.white,
    },
    text: {
        margin: '0 0 24px',
        maxWidth: '500px',
        color: REDESIGN_COLORS.body,
    },
    textOnDark: {
        color: REDESIGN_COLORS.bodyOnDark,
    },
    bullets: {
        display: 'grid',
        gap: '11px',
        margin: 0,
        padding: 0,
        listStyle: 'none',
        // Keeps the copy above the decorative blob.
        position: 'relative',
        zIndex: 2,
        color: REDESIGN_COLORS.body,
        fontWeight: REDESIGN_FONTS.weightStrong,
    },
    bulletsOnDark: {
        color: REDESIGN_COLORS.bodyOnDark,
    },
    bulletsWithCta: {
        margin: '0 0 26px',
    },
    bullet: {
        display: 'flex',
        gap: '10px',
    },
    tick: {
        color: REDESIGN_COLORS.primary,
        fontWeight: REDESIGN_FONTS.weightHeading,
    },
    cta: {
        position: 'relative',
        zIndex: 2,
        alignSelf: 'flex-start',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '48px',
        // Pins the CTA to the bottom so cards of unequal length stay aligned.
        marginTop: 'auto',
        padding: '0 22px',
        border: `1px solid ${REDESIGN_COLORS.amberBorderStrong}`,
        borderRadius: '999px',
        color: REDESIGN_COLORS.dark,
        background: REDESIGN_COLORS.white,
        fontWeight: REDESIGN_FONTS.weightHeading,
        '&:hover': {
            borderColor: REDESIGN_COLORS.primary,
            color: REDESIGN_COLORS.link,
        },
    },
    blob: {
        position: 'absolute',
        width: '210px',
        height: '210px',
        right: '-80px',
        bottom: '-90px',
        borderRadius: '50%',
        pointerEvents: 'none',
    },
    disc: {
        background: 'rgba(255, 200, 61, 0.55)',
    },
    ringStrong: {
        border: '38px solid rgba(16, 169, 179, 0.35)',
    },
    ringSoft: {
        border: '38px solid rgba(16, 169, 179, 0.28)',
    },
}));

/**
 * The `#contexte` band: three cards for the three ways VBoard gets used. The
 * middle card is inverted, and the third carries a CTA.
 */
const HomeContexts = () => {
    const classes = useStyles();

    return (
        <LandingSection id="contexte" paddingTop={48} paddingBottom={64}>
            <SectionHeading
                eyebrow={<FormattedMessage id="redesign.home.contexts.eyebrow" />}
                title={<FormattedMessage id="redesign.home.contexts.title" />}
                lead={<FormattedMessage id="redesign.home.contexts.lead" />}
            />

            <div className={classes.grid}>
                {CARDS.map(({ key, id, theme, bullets, blob, cta }) => {
                    const dark = theme === 'dark';

                    return (
                        <article key={key} id={id} className={`${classes.card} ${classes[theme]}`}>
                            <SectionEyebrow
                                variant="card"
                                className={`${classes.eyebrow} ${dark ? classes.eyebrowOnDark : ''}`}
                            >
                                <FormattedMessage id={`redesign.home.contexts.${key}.label`} />
                            </SectionEyebrow>

                            <h3 className={`${classes.title} ${dark ? classes.titleOnDark : ''}`}>
                                <FormattedMessage id={`redesign.home.contexts.${key}.title`} />
                            </h3>

                            <p className={`${classes.text} ${dark ? classes.textOnDark : ''}`}>
                                <FormattedMessage id={`redesign.home.contexts.${key}.text`} />
                            </p>

                            <ul
                                className={[
                                    classes.bullets,
                                    dark ? classes.bulletsOnDark : '',
                                    cta ? classes.bulletsWithCta : '',
                                ]
                                    .filter(Boolean)
                                    .join(' ')}
                            >
                                {Array.from({ length: bullets }, (_, i) => (
                                    <li key={i} className={classes.bullet}>
                                        <span aria-hidden="true" className={classes.tick}>
                                            ✓
                                        </span>
                                        <FormattedMessage id={`redesign.home.contexts.${key}.bullet${i + 1}`} />
                                    </li>
                                ))}
                            </ul>

                            {/* The design links this to an in-page #scoli anchor; the
                                separate ForSchools route is its equivalent here. */}
                            {cta ? (
                                <Link to={cta} className={classes.cta}>
                                    <FormattedMessage id={`redesign.home.contexts.${key}.cta`} />
                                </Link>
                            ) : null}

                            <div aria-hidden="true" className={`${classes.blob} ${classes[blob]}`} />
                        </article>
                    );
                })}
            </div>
        </LandingSection>
    );
};

export default HomeContexts;
